import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, X, FileSpreadsheet, Settings, Search, Filter, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const LabInventory = () => {
  const navigate = useNavigate();
  
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [activeSheetId, setActiveSheetId] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('labInventoryActiveSheet') || null;
  });
  const [sheetRows, setSheetRows] = useState({});
  const [users, setUsers] = useState([]);
  const [editingCell, setEditingCell] = useState({ rowId: null, field: null });
  const [editValue, setEditValue] = useState('');
  const [showAddSheetModal, setShowAddSheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState('');
  const [newSheetColumns, setNewSheetColumns] = useState([
    { key: '', label: '', type: 'text', options: '' }
  ]);
  const [showEditSheetModal, setShowEditSheetModal] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [editSheetName, setEditSheetName] = useState('');
  const [editSheetColumns, setEditSheetColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  // Drag and drop state
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Refs for auto-scrolling
  const newSheetColumnsEndRef = useRef(null);
  const editSheetColumnsEndRef = useRef(null);
  const newSheetScrollContainerRef = useRef(null);
  const editSheetScrollContainerRef = useRef(null);
  const dragScrollIntervalRef = useRef(null);
  const [showAllSheetsView, setShowAllSheetsView] = useState(() => {
    // Only show All Sheets if there's no saved active sheet AND no saved open sheets
    const savedActiveSheet = localStorage.getItem('labInventoryActiveSheet');
    const savedOpenSheets = localStorage.getItem('labInventoryOpenSheets');
    return !savedActiveSheet && !savedOpenSheets;
  });
  const [openSheetIds, setOpenSheetIds] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('labInventoryOpenSheets');
    return saved ? JSON.parse(saved) : [];
  });

  const activeSheet = sheets.find(s => s.id === activeSheetId);
  const allActiveRows = sheetRows[activeSheetId] || [];
  const openSheets = sheets.filter(s => openSheetIds.includes(s.id));
  
  // Filter rows based on search and column filters
  const activeRows = useMemo(() => {
    let filtered = allActiveRows;
    
    // Apply search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(row => {
        return Object.values(row.data || {}).some(value => 
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }
    
    // Apply column filters
    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (filterValue && filterValue.trim()) {
        filtered = filtered.filter(row => {
          const cellValue = String(row.data?.[columnKey] || '');
          return cellValue.toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });
    
    return filtered;
  }, [allActiveRows, searchQuery, columnFilters]);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        
        // Load user role
        const { data: userData, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (!error && userData) {
          setUserRole(userData.role);
        }
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        // Use current user instead of querying profiles table
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUsers([user.email]);
        } else {
          setUsers([]);
        }
      } catch (error) {
        setUsers([]);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const loadAllData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadSheets(), loadAllSheetRows()]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();

    const sheetsChannel = supabase
      .channel('lab_sheets_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lab_sheets' }, loadSheets)
      .subscribe();

    const rowsChannel = supabase
      .channel('lab_sheet_rows_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lab_sheet_rows' }, loadAllSheetRows)
      .subscribe();

    return () => {
      supabase.removeChannel(sheetsChannel);
      supabase.removeChannel(rowsChannel);
      // Clean up scroll interval
      if (dragScrollIntervalRef.current) {
        clearInterval(dragScrollIntervalRef.current);
      }
    };
  }, [currentUser]);

  const loadSheets = async () => {
    try {
      const { data, error } = await supabase
        .from('lab_sheets')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setSheets(data || []);
      
      // Load open sheets from localStorage or default to all sheets
      const savedOpenSheets = localStorage.getItem('labInventoryOpenSheets');
      const savedActiveSheet = localStorage.getItem('labInventoryActiveSheet');
      
      if (savedOpenSheets && savedActiveSheet) {
        try {
          const openIds = JSON.parse(savedOpenSheets);
          // Validate that saved IDs exist in loaded sheets
          const validOpenIds = openIds.filter(id => data.some(s => s.id === id));
          
          if (validOpenIds.length > 0) {
            setOpenSheetIds(validOpenIds);
            
            // Check if savedActiveSheet is valid
            if (data.some(s => s.id === savedActiveSheet)) {
              setActiveSheetId(savedActiveSheet);
              setShowAllSheetsView(false);
            } else if (validOpenIds.length > 0) {
              setActiveSheetId(validOpenIds[0]);
              setShowAllSheetsView(false);
              localStorage.setItem('labInventoryActiveSheet', validOpenIds[0]);
            }
          } else {
            // No valid open sheets, default to all sheets
            const allIds = data.map(s => s.id);
            setOpenSheetIds(allIds);
            setActiveSheetId(data[0].id);
            setShowAllSheetsView(false);
            localStorage.setItem('labInventoryOpenSheets', JSON.stringify(allIds));
            localStorage.setItem('labInventoryActiveSheet', data[0].id);
          }
        } catch (e) {
          // Error parsing localStorage, reset to defaults
          const allIds = data.map(s => s.id);
          setOpenSheetIds(allIds);
          setActiveSheetId(data[0].id);
          setShowAllSheetsView(false);
          localStorage.setItem('labInventoryOpenSheets', JSON.stringify(allIds));
          localStorage.setItem('labInventoryActiveSheet', data[0].id);
        }
      } else if (data && data.length > 0) {
        // Default: open all sheets
        const allIds = data.map(s => s.id);
        setOpenSheetIds(allIds);
        setActiveSheetId(data[0].id);
        setShowAllSheetsView(false);
        localStorage.setItem('labInventoryOpenSheets', JSON.stringify(allIds));
        localStorage.setItem('labInventoryActiveSheet', data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load sheets');
    }
  };
  
  // Reset filters when changing sheets
  useEffect(() => {
    setSearchQuery('');
    setColumnFilters({});
    setShowFilters(false);
  }, [activeSheetId]);
  
  // Save open sheets state to localStorage
  useEffect(() => {
    if (openSheetIds.length > 0) {
      localStorage.setItem('labInventoryOpenSheets', JSON.stringify(openSheetIds));
    }
  }, [openSheetIds]);
  
  // Save active sheet to localStorage
  useEffect(() => {
    if (activeSheetId) {
      localStorage.setItem('labInventoryActiveSheet', activeSheetId);
    }
  }, [activeSheetId]);
  
  // (Removed duplicate/empty loadAllSheetRows function that caused a syntax error)

  const loadAllSheetRows = async () => {
    try {
      const { data, error } = await supabase
        .from('lab_sheet_rows')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const newGrouped = {};
      (data || []).forEach(row => {
        if (!newGrouped[row.sheet_id]) {
          newGrouped[row.sheet_id] = [];
        }
        newGrouped[row.sheet_id].push(row);
      });
      
      // Preserve existing order by merging instead of replacing
      setSheetRows(prevRows => {
        const merged = {};
        
        // For each sheet, preserve order of existing rows and update/add new ones
        Object.keys(newGrouped).forEach(sheetId => {
          const existingRows = prevRows[sheetId] || [];
          const newRows = newGrouped[sheetId];
          
          // Create a map of new rows by ID for quick lookup
          const newRowsMap = new Map(newRows.map(r => [r.id, r]));
          
          // Keep existing rows in their current order, update if changed
          const updatedRows = existingRows
            .map(existingRow => newRowsMap.get(existingRow.id))
            .filter(row => row !== undefined); // Remove deleted rows
          
          // Add newly created rows that weren't in the existing list
          const existingIds = new Set(existingRows.map(r => r.id));
          const newlyAddedRows = newRows.filter(r => !existingIds.has(r.id));
          
          // Put new rows at the bottom
          merged[sheetId] = [...updatedRows, ...newlyAddedRows];
        });
        
        // Keep sheets that still exist
        Object.keys(prevRows).forEach(sheetId => {
          if (!merged[sheetId] && newGrouped[sheetId]) {
            merged[sheetId] = newGrouped[sheetId];
          }
        });
        
        return merged;
      });
    } catch (error) {
      toast.error('Failed to load sheet data');
    }
  };

  const handleAddRow = async () => {
    if (!currentUser || !activeSheetId) return;

    try {
      const emptyData = {};
      if (activeSheet && activeSheet.columns) {
        activeSheet.columns.forEach(col => {
          if (col.type === 'timestamp') {
            emptyData[col.key] = new Date().toISOString();
          } else if (col.type === 'dropdown') {
            emptyData[col.key] = col.options?.[0] || '';
          } else {
            emptyData[col.key] = '';
          }
        });
      }

      const { error } = await supabase
        .from('lab_sheet_rows')
        .insert({
          sheet_id: activeSheetId,
          data: emptyData,
          created_by: currentUser.id
        });

      if (error) throw error;
      toast.success('Row added successfully');
    } catch (error) {
      toast.error('Failed to add row');
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      const { error } = await supabase
        .from('lab_sheet_rows')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Row deleted successfully');
    } catch (error) {
      toast.error('Failed to delete row');
    }
  };

  const handleCellUpdate = async (rowId, field, value) => {
    try {
      const currentRow = allActiveRows.find(r => r.id === rowId);
      if (!currentRow) return;

      const updatedData = {
        ...currentRow.data,
        [field]: value
      };

      const { error } = await supabase
        .from('lab_sheet_rows')
        .update({
          data: updatedData,
          updated_by: currentUser?.id
        })
        .eq('id', rowId);

      if (error) throw error;
      
      // Check if this is the "WHO IS TAKING?" field being updated and trigger checkout
      const whoIsTakingKey = activeSheet?.columns.find(col => 
        col.label.toLowerCase().includes('who is taking') || 
        col.label.toLowerCase().includes('who_is_taking')
      )?.key;
      
      if (field === whoIsTakingKey && value && value.trim()) {
        // Trigger checkout process
        await processCheckout(rowId, updatedData);
      }
    } catch (error) {
      toast.error('Failed to update cell');
    }
  };
  
  const processCheckout = async (rowId, rowData) => {
    // Helper function to clear the WHO IS TAKING field on error
    const clearWhoIsTakingField = async (whoKey) => {
      try {
        const clearedData = {
          ...rowData,
          [whoKey]: ''
        };
        await supabase
          .from('lab_sheet_rows')
          .update({
            data: clearedData,
            updated_by: currentUser?.id
          })
          .eq('id', rowId);
      } catch (err) {
        console.error('Failed to clear WHO IS TAKING field:', err);
      }
    };
    
    try {
      // Find the column keys for checkout fields
      const takeQtyKey = activeSheet?.columns.find(col => 
        col.label.toLowerCase().includes('take qty') || 
        col.label.toLowerCase().includes('take_qty')
      )?.key;
      
      const reasonKey = activeSheet?.columns.find(col => 
        col.label.toLowerCase().includes('reason')
      )?.key;
      
      const whoIsTakingKey = activeSheet?.columns.find(col => 
        col.label.toLowerCase().includes('who is taking') || 
        col.label.toLowerCase().includes('who_is_taking')
      )?.key;
      
      const currentStockKey = activeSheet?.columns.find(col => 
        col.label.toLowerCase().includes('current stock') || 
        col.label.toLowerCase().includes('current_stock')
      )?.key;
      
      const itemNameKey = activeSheet?.columns.find(col => 
        col.label.toLowerCase().includes('item') || 
        col.label.toLowerCase().includes('name')
      )?.key;
      
      // Validate required fields exist
      if (!takeQtyKey || !reasonKey || !whoIsTakingKey || !currentStockKey) {
        toast.error('Missing required columns for checkout');
        if (whoIsTakingKey) await clearWhoIsTakingField(whoIsTakingKey);
        return;
      }
      
      const takeQty = parseFloat(rowData[takeQtyKey]) || 0;
      const reason = rowData[reasonKey] || '';
      const whoIsTaking = rowData[whoIsTakingKey] || '';
      const currentStock = parseFloat(rowData[currentStockKey]) || 0;
      const itemName = rowData[itemNameKey] || 'Item';
      
      // Validation with field clearing on error
      if (!takeQty || takeQty <= 0) {
        toast.error('Please enter a valid quantity to take');
        await clearWhoIsTakingField(whoIsTakingKey);
        return;
      }
      
      if (!reason.trim()) {
        toast.error('Please enter a reason for taking the item');
        await clearWhoIsTakingField(whoIsTakingKey);
        return;
      }
      
      if (!whoIsTaking.trim()) {
        toast.error('Please specify who is taking the item');
        await clearWhoIsTakingField(whoIsTakingKey);
        return;
      }
      
      // Check if sufficient stock is available
      if (currentStock < takeQty) {
        toast.error(`Insufficient stock! Current stock: ${currentStock}, Requested: ${takeQty}`);
        await clearWhoIsTakingField(whoIsTakingKey);
        return;
      }
      
      // Find the existing CheckOut sheet (id: 3)
      const { data: checkoutSheet, error: sheetError } = await supabase
        .from('lab_sheets')
        .select('*')
        .eq('id', 3)
        .single();
      
      if (sheetError || !checkoutSheet) {
        toast.error('CheckOut sheet not found');
        await clearWhoIsTakingField(whoIsTakingKey);
        return;
      }
      
      // Calculate new stock
      const newStock = currentStock - takeQty;
      
      // Create checkout entry matching the CheckOut sheet columns:
      // Timestamp, Item Name, Person Name, Qty Taken, Stock Remaining, Reason for Checkout
      const checkoutData = {
        timestamp: new Date().toISOString(),
        item_name: itemName,
        person_name: whoIsTaking,
        qty_taken: takeQty.toString(),
        stock_remaining: newStock.toString(),
        reason_for_checkout: reason
      };
      
      const { error: checkoutError } = await supabase
        .from('lab_sheet_rows')
        .insert({
          sheet_id: checkoutSheet.id,
          data: checkoutData,
          created_by: currentUser.id
        });
        
      if (checkoutError) throw checkoutError;
      
      // Update the inventory row: update stock and clear checkout fields
      const updatedRowData = {
        ...rowData,
        [currentStockKey]: newStock.toString(),
        [takeQtyKey]: '',
        [reasonKey]: '',
        [whoIsTakingKey]: ''
      };
      
      const { error: updateError } = await supabase
        .from('lab_sheet_rows')
        .update({
          data: updatedRowData,
          updated_by: currentUser.id
        })
        .eq('id', rowId);
        
      if (updateError) throw updateError;
      
      // Show success message
      toast.success(`Checkout added: ${whoIsTaking} took ${takeQty} ${itemName}`, {
        duration: 4000
      });
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to process checkout: ' + (error.message || 'Unknown error'));
      
      // Clear WHO IS TAKING field on any error
      try {
        const whoIsTakingKey = activeSheet?.columns.find(col => 
          col.label.toLowerCase().includes('who is taking') || 
          col.label.toLowerCase().includes('who_is_taking')
        )?.key;
        
        if (whoIsTakingKey) {
          const clearedData = {
            ...rowData,
            [whoIsTakingKey]: ''
          };
          await supabase
            .from('lab_sheet_rows')
            .update({
              data: clearedData,
              updated_by: currentUser?.id
            })
            .eq('id', rowId);
        }
      } catch (clearError) {
        console.error('Failed to clear WHO IS TAKING field:', clearError);
      }
    }
  };

  const startEdit = (rowId, field, currentValue) => {
    setEditingCell({ rowId, field });
    setEditValue(currentValue || '');
  };

  const saveEdit = async () => {
    if (editingCell.rowId && editingCell.field) {
      await handleCellUpdate(editingCell.rowId, editingCell.field, editValue);
      setEditingCell({ rowId: null, field: null });
      setEditValue('');
    }
  };

  const cancelEdit = () => {
    setEditingCell({ rowId: null, field: null });
    setEditValue('');
  };

  const handleAddSheet = async () => {
    if (!currentUser || !newSheetName.trim()) {
      toast.error('Sheet name is required');
      return;
    }

    const validColumns = newSheetColumns.filter(col => col.key && col.label);
    if (validColumns.length === 0) {
      toast.error('At least one column is required');
      return;
    }

    try {
      const maxOrder = sheets.length > 0 ? Math.max(...sheets.map(s => s.order_index)) : 0;
      
      const { error } = await supabase
        .from('lab_sheets')
        .insert({
          name: newSheetName.trim(),
          columns: validColumns.map(col => ({
            key: col.key,
            label: col.label,
            type: col.type,
            options: col.type === 'dropdown' && col.options 
              ? col.options.split(',').map(v => v.trim()).filter(v => v)
              : undefined
          })),
          order_index: maxOrder + 1,
          created_by: currentUser.id
        });

      if (error) throw error;
      
      toast.success('Sheet created successfully');
      setShowAddSheetModal(false);
      setNewSheetName('');
      setNewSheetColumns([{ key: '', label: '', type: 'text', options: '' }]);
    } catch (error) {
      toast.error(error.message || 'Failed to create sheet');
    }
  };

  const addColumnToNewSheet = () => {
    setNewSheetColumns([...newSheetColumns, { key: '', label: '', type: 'text', options: '' }]);
    // Auto-scroll to the new column after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (newSheetColumnsEndRef.current) {
        newSheetColumnsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const removeColumnFromNewSheet = (index) => {
    setNewSheetColumns(newSheetColumns.filter((_, i) => i !== index));
  };

  const updateNewSheetColumn = (index, field, value) => {
    const updated = [...newSheetColumns];
    updated[index][field] = value;
    if (field === 'label') {
      updated[index].key = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }
    setNewSheetColumns(updated);
  };

  const handleDeleteSheet = async (sheetId, sheetName) => {
    if (!window.confirm(`Are you sure you want to delete the "${sheetName}" sheet? All data in this sheet will be permanently deleted.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('lab_sheets')
        .delete()
        .eq('id', sheetId);

      if (error) throw error;
      
      toast.success('Sheet deleted successfully');
      
      // Remove from open sheets
      setOpenSheetIds(prev => prev.filter(id => id !== sheetId));
      
      if (activeSheetId === sheetId) {
        const remainingSheets = sheets.filter(s => s.id !== sheetId);
        if (remainingSheets.length > 0) {
          setActiveSheetId(remainingSheets[0].id);
        } else {
          setActiveSheetId(null);
        }
      }
    } catch (error) {
      toast.error('Failed to delete sheet');
    }
  };
  
  const handleOpenSheet = (sheetId) => {
    // Check if this is the CheckOut sheet (name contains "CheckOut" or "checkout")
    const sheet = sheets.find(s => s.id === sheetId);
    const isCheckOutSheet = sheet && (sheet.name.toLowerCase().includes('checkout') || sheet.id === 3);
    
    // If it's CheckOut sheet and user is not admin, show error
    if (isCheckOutSheet && userRole !== 'admin') {
      toast.error('Only admins can access the CheckOut sheet');
      return;
    }
    
    if (!openSheetIds.includes(sheetId)) {
      setOpenSheetIds(prev => {
        const newIds = [...prev, sheetId];
        localStorage.setItem('labInventoryOpenSheets', JSON.stringify(newIds));
        return newIds;
      });
    }
    setActiveSheetId(sheetId);
    localStorage.setItem('labInventoryActiveSheet', sheetId);
    setShowAllSheetsView(false);
  };
  
  const handleCloseSheet = (sheetId) => {
    setOpenSheetIds(prev => {
      const newIds = prev.filter(id => id !== sheetId);
      localStorage.setItem('labInventoryOpenSheets', JSON.stringify(newIds));
      return newIds;
    });
    if (activeSheetId === sheetId) {
      const remaining = openSheetIds.filter(id => id !== sheetId);
      if (remaining.length > 0) {
        setActiveSheetId(remaining[0]);
        localStorage.setItem('labInventoryActiveSheet', remaining[0]);
      } else {
        setShowAllSheetsView(true);
        setActiveSheetId(null);
        localStorage.removeItem('labInventoryActiveSheet');
      }
    }
  };

  const openEditSheetModal = (sheet) => {
    setEditingSheet(sheet);
    setEditSheetName(sheet.name);
    setEditSheetColumns(sheet.columns.map(col => ({
      ...col,
      options: col.options ? col.options.join(', ') : ''
    })));
    setShowEditSheetModal(true);
  };

  const updateEditSheetColumn = (index, field, value) => {
    const updated = [...editSheetColumns];
    updated[index][field] = value;
    if (field === 'label') {
      updated[index].key = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }
    setEditSheetColumns(updated);
  };

  const addColumnToEditSheet = () => {
    setEditSheetColumns([...editSheetColumns, { key: '', label: '', type: 'text', options: '' }]);
    // Auto-scroll to the new column after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (editSheetColumnsEndRef.current) {
        editSheetColumnsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const removeColumnFromEditSheet = (index) => {
    setEditSheetColumns(editSheetColumns.filter((_, i) => i !== index));
  };

  // Drag and Drop Handlers for Column Reordering
  const handleDragStart = (e, index) => {
    setDraggedColumnIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ''); // For Firefox compatibility
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
    
    // Auto-scroll while dragging
    const container = editSheetScrollContainerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY;
      const scrollThreshold = 100; // pixels from edge to trigger scroll
      const scrollSpeed = 10;
      
      // Clear any existing scroll interval
      if (dragScrollIntervalRef.current) {
        clearInterval(dragScrollIntervalRef.current);
      }
      
      // Scroll down if near bottom
      if (mouseY > rect.bottom - scrollThreshold) {
        dragScrollIntervalRef.current = setInterval(() => {
          container.scrollTop += scrollSpeed;
        }, 16);
      }
      // Scroll up if near top
      else if (mouseY < rect.top + scrollThreshold) {
        dragScrollIntervalRef.current = setInterval(() => {
          container.scrollTop -= scrollSpeed;
        }, 16);
      }
    }
  };

  const handleDragLeave = (e) => {
    // Only clear dragOverIndex if we're leaving the entire draggable area
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    // Clear scroll interval
    if (dragScrollIntervalRef.current) {
      clearInterval(dragScrollIntervalRef.current);
      dragScrollIntervalRef.current = null;
    }
    
    if (draggedColumnIndex === null || draggedColumnIndex === dropIndex) {
      setDraggedColumnIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...editSheetColumns];
    const draggedColumn = newColumns[draggedColumnIndex];
    
    // Remove the dragged column from its original position
    newColumns.splice(draggedColumnIndex, 1);
    
    // Insert the dragged column at the new position
    newColumns.splice(dropIndex, 0, draggedColumn);
    
    setEditSheetColumns(newColumns);
    setDraggedColumnIndex(null);
    setDragOverIndex(null);
    
    toast.success('Column reordered successfully');
  };

  const handleDragEnd = () => {
    setDraggedColumnIndex(null);
    setDragOverIndex(null);
    
    // Clear scroll interval
    if (dragScrollIntervalRef.current) {
      clearInterval(dragScrollIntervalRef.current);
      dragScrollIntervalRef.current = null;
    }
  };

  // Drag and Drop Handlers for New Sheet Columns
  const handleNewSheetDragStart = (e, index) => {
    setDraggedColumnIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleNewSheetDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
    
    // Auto-scroll while dragging
    const container = newSheetScrollContainerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY;
      const scrollThreshold = 100; // pixels from edge to trigger scroll
      const scrollSpeed = 10;
      
      // Clear any existing scroll interval
      if (dragScrollIntervalRef.current) {
        clearInterval(dragScrollIntervalRef.current);
      }
      
      // Scroll down if near bottom
      if (mouseY > rect.bottom - scrollThreshold) {
        dragScrollIntervalRef.current = setInterval(() => {
          container.scrollTop += scrollSpeed;
        }, 16);
      }
      // Scroll up if near top
      else if (mouseY < rect.top + scrollThreshold) {
        dragScrollIntervalRef.current = setInterval(() => {
          container.scrollTop -= scrollSpeed;
        }, 16);
      }
    }
  };

  const handleNewSheetDrop = (e, dropIndex) => {
    e.preventDefault();
    
    // Clear scroll interval
    if (dragScrollIntervalRef.current) {
      clearInterval(dragScrollIntervalRef.current);
      dragScrollIntervalRef.current = null;
    }
    
    if (draggedColumnIndex === null || draggedColumnIndex === dropIndex) {
      setDraggedColumnIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...newSheetColumns];
    const draggedColumn = newColumns[draggedColumnIndex];
    
    newColumns.splice(draggedColumnIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);
    
    setNewSheetColumns(newColumns);
    setDraggedColumnIndex(null);
    setDragOverIndex(null);
    
    toast.success('Column reordered successfully');
  };

  const handleEditSheet = async () => {
    if (!currentUser || !editSheetName.trim()) {
      toast.error('Sheet name is required');
      return;
    }

    const validColumns = editSheetColumns.filter(col => col.key && col.label);
    if (validColumns.length === 0) {
      toast.error('At least one column is required');
      return;
    }

    try {
      const { error } = await supabase
        .from('lab_sheets')
        .update({
          name: editSheetName.trim(),
          columns: validColumns.map(col => ({
            key: col.key,
            label: col.label,
            type: col.type,
            options: col.type === 'dropdown' && col.options 
              ? col.options.split(',').map(v => v.trim()).filter(v => v)
              : undefined
          })),
          updated_by: currentUser.id
        })
        .eq('id', editingSheet.id);

      if (error) throw error;
      
      toast.success('Sheet updated successfully');
      setShowEditSheetModal(false);
      setEditingSheet(null);
      setEditSheetName('');
      setEditSheetColumns([]);
    } catch (error) {
      toast.error(error.message || 'Failed to update sheet');
    }
  };

  const renderCell = (row, column) => {
    const isEditing = editingCell.rowId === row.id && editingCell.field === column.key;
    const value = row.data?.[column.key] || '';

    if (column.type === 'timestamp' && !isEditing) {
      return (
        <div className="w-full px-2 py-1.5 min-h-[28px] text-gray-300">
          {value ? format(new Date(value), 'MMM dd, yyyy hh:mm a') : '-'}
        </div>
      );
    }

    if (isEditing) {
      if (column.type === 'dropdown') {
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            autoFocus
            className="w-full px-2 py-1 bg-[#0f1419] border-2 border-green-500 rounded text-sm text-white focus:outline-none [&>option]:bg-[#1a1f2e]"
          >
            {(column.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      } else if (column.type === 'user_dropdown') {
        return (
          <input
            type="text"
            list={`users-${row.id}-${column.key}`}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            autoFocus
            className="w-full px-2 py-1 bg-[#0f1419] border-2 border-green-500 rounded text-sm text-white focus:outline-none"
          />
        );
      }
      
      return (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit();
            if (e.key === 'Escape') cancelEdit();
          }}
          autoFocus
          className="w-full px-2 py-1 bg-[#0f1419] border-2 border-green-500 rounded text-sm text-white focus:outline-none"
        />
      );
    }

    return (
      <div
        className="w-full px-2 py-1.5 cursor-text hover:bg-[#1a1f2e] rounded min-h-[28px] text-gray-200"
        onDoubleClick={() => column.type !== 'timestamp' && startEdit(row.id, column.key, value)}
        title={column.type !== 'timestamp' ? 'Double-click to edit' : ''}
      >
        {value || <span className="text-gray-500">-</span>}
      </div>
    );
  };

  const renderSheet = () => {
    if (!activeSheet) return null;

    return (
      <div className="relative h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#1a1f2e] border-b-2 border-gray-600">
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-300 border-r border-gray-600 w-8 bg-[#1a1f2e]">#</th>
                {activeSheet.columns.map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-300 border-r border-gray-600 min-w-[150px] bg-[#1a1f2e]"
                  >
                    {col.label}
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-300 w-12 bg-[#1a1f2e]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row, idx) => (
                <tr key={row.id} className="border-b border-gray-700 hover:bg-[#1a1f2e]">
                  <td className="px-3 py-1 text-xs text-gray-400 border-r border-gray-700">{idx + 1}</td>
                  {activeSheet.columns.map(col => (
                    <td key={col.key} className="border-r border-gray-700 text-sm text-gray-200">
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center">
                    <button
                      onClick={() => handleDeleteRow(row.id)}
                      className="p-1 hover:bg-red-900 rounded text-red-400 hover:text-red-300"
                      title="Delete row"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-b border-gray-700 hover:bg-[#1a1f2e]">
                <td colSpan={activeSheet.columns.length + 2} className="px-3 py-2">
                  <button
                    onClick={handleAddRow}
                    className="flex items-center gap-2 py-2 px-3 text-green-400 hover:text-green-300 hover:bg-[#1a1f2e] rounded-lg transition-colors"
                    title="Add new row"
                  >
                    <Plus size={18} />
                    <span className="text-sm font-medium">Add Row</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full w-full flex flex-col bg-[#0f1419]">

      <div className="sticky top-0 z-10 flex items-center border-b border-gray-700 bg-[#1a1f2e] px-4 overflow-x-auto">
        <button
          onClick={() => setShowAllSheetsView(true)}
          className={`flex items-center gap-1.5 px-3 py-2.5 mr-2 border-b-2 transition-colors ${
            showAllSheetsView
              ? 'border-green-500 bg-[#0f1419] text-green-400'
              : 'border-transparent hover:bg-gray-700 text-gray-400 hover:text-gray-200'
          }`}
        >
          <FileSpreadsheet size={14} />
          <span className="text-sm font-medium whitespace-nowrap">All Sheets</span>
        </button>
        {openSheets.map(sheet => (
          <div
            key={sheet.id}
            className={`flex items-center gap-2 px-3 py-2.5 border-b-2 transition-colors ${
              activeSheetId === sheet.id && !showAllSheetsView
                ? 'border-green-500 bg-[#0f1419]'
                : 'border-transparent hover:bg-gray-700'
            }`}
          >
            <button
              onClick={() => {
                setActiveSheetId(sheet.id);
                setShowAllSheetsView(false);
              }}
              className={`text-sm font-medium whitespace-nowrap ${
                activeSheetId === sheet.id && !showAllSheetsView ? 'text-green-400' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {sheet.name} ({(sheetRows[sheet.id] || []).length})
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openEditSheetModal(sheet);
              }}
              className="p-1 hover:bg-gray-600 rounded text-gray-400 hover:text-blue-400 transition-colors"
              title="Edit sheet"
            >
              <Settings size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCloseSheet(sheet.id);
              }}
              className="p-1 hover:bg-gray-600 rounded text-gray-400 hover:text-gray-300 transition-colors"
              title="Close sheet"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          onClick={() => setShowAddSheetModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 ml-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded transition-colors text-sm font-medium whitespace-nowrap"
          title="Add new sheet"
        >
          <Plus size={16} />
          Add Sheet
        </button>
      </div>

      <div className="flex-1 overflow-auto bg-[#0f1419]">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        ) : showAllSheetsView ? (
          <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-4">All Sheets</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sheets.map(sheet => {
                const isCheckOutSheet = sheet.name.toLowerCase().includes('checkout') || sheet.id === 3;
                const canAccess = !isCheckOutSheet || userRole === 'admin';
                
                return (
                  <div
                    key={sheet.id}
                    className="bg-[#1a1f2e] border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {sheet.name}
                          {isCheckOutSheet && <span className="ml-2 text-xs text-orange-400">(Admin Only)</span>}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {(sheetRows[sheet.id] || []).length} rows · {sheet.columns?.length || 0} columns
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenSheet(sheet.id)}
                        disabled={!canAccess}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          canAccess
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <FileSpreadsheet size={16} />
                        {openSheetIds.includes(sheet.id) ? 'Switch to' : 'Open'}
                      </button>
                    <button
                      onClick={() => handleDeleteSheet(sheet.id, sheet.name)}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                      title="Delete sheet"
                    >
                      <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="min-w-max p-4 h-full flex flex-col">
            {activeSheet && (
              <div className="mb-3 space-y-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-8 pr-3 py-1.5 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    />
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      showFilters 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-[#1a1f2e] hover:bg-gray-700 text-gray-300 border border-gray-600'
                    }`}
                  >
                    <Filter size={14} />
                    Filters
                  </button>
                  {(searchQuery || Object.keys(columnFilters).length > 0) && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setColumnFilters({});
                      }}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                {showFilters && activeSheet.columns.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 bg-[#1a1f2e] rounded-lg border border-gray-700">
                    {activeSheet.columns.map(col => (
                      <div key={col.key}>
                        <label className="block text-xs text-gray-400 mb-1.5">{col.label}</label>
                        {col.type === 'dropdown' ? (
                          <select
                            value={columnFilters[col.key] || ''}
                            onChange={(e) => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                            className="w-full px-3 py-1.5 bg-[#0f1419] border border-gray-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">All</option>
                            {(col.options || []).map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : col.type === 'user_dropdown' ? (
                          <select
                            value={columnFilters[col.key] || ''}
                            onChange={(e) => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                            className="w-full px-3 py-1.5 bg-[#0f1419] border border-gray-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">All</option>
                            {users.map(user => (
                              <option key={user} value={user}>{user}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={columnFilters[col.key] || ''}
                            onChange={(e) => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                            placeholder={`Filter ${col.label.toLowerCase()}...`}
                            className="w-full px-3 py-1.5 bg-[#0f1419] border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="text-xs text-gray-500">
                  {activeRows.length} of {allActiveRows.length} rows
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0">
              {renderSheet()}
            </div>
          </div>
        )}
      </div>

      {showAddSheetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1a1f2e] rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Create New Sheet</h2>
              <button
                onClick={() => {
                  setShowAddSheetModal(false);
                  setNewSheetName('');
                  setNewSheetColumns([{ key: '', label: '', type: 'text', options: '' }]);
                }}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400"
              >
                <X size={20} />
              </button>
            </div>

            <div ref={newSheetScrollContainerRef} className="p-6 space-y-6 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sheet Name
                </label>
                <input
                  type="text"
                  value={newSheetName}
                  onChange={(e) => setNewSheetName(e.target.value)}
                  placeholder="e.g., Equipment, Materials, etc."
                  className="w-full px-4 py-2 bg-[#0f1419] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-300">
                    Columns
                  </label>
                  <button
                    onClick={addColumnToNewSheet}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <Plus size={16} />
                    Add Column
                  </button>
                </div>

                <div className="space-y-3">
                  {newSheetColumns.map((col, index) => (
                    <div 
                      key={index} 
                      className={`bg-[#0f1419] p-4 rounded-lg border transition-all duration-200 ${
                        draggedColumnIndex === index 
                          ? 'border-green-500 opacity-50 scale-95' 
                          : dragOverIndex === index 
                            ? 'border-blue-500 border-dashed bg-blue-500/5' 
                            : 'border-gray-700'
                      }`}
                      draggable={true}
                      onDragStart={(e) => handleNewSheetDragStart(e, index)}
                      onDragOver={(e) => handleNewSheetDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleNewSheetDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-1 flex justify-center">
                          <div 
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-600 rounded transition-colors"
                            title="Drag to reorder"
                          >
                            <GripVertical size={16} className="text-gray-400" />
                          </div>
                        </div>

                        <div className="col-span-4">
                          <label className="block text-xs text-gray-400 mb-1">Column Label</label>
                          <input
                            type="text"
                            value={col.label}
                            onChange={(e) => updateNewSheetColumn(index, 'label', e.target.value)}
                            placeholder="e.g., Item Name"
                            className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        <div className="col-span-3">
                          <label className="block text-xs text-gray-400 mb-1">Type</label>
                          <select
                            value={col.type}
                            onChange={(e) => updateNewSheetColumn(index, 'type', e.target.value)}
                            className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="text">Text</option>
                            <option value="dropdown">Dropdown</option>
                            <option value="user_dropdown">User Dropdown</option>
                            <option value="timestamp">Timestamp</option>
                          </select>
                        </div>

                        <div className="col-span-3">
                          <label className="block text-xs text-gray-400 mb-1">
                            Options {col.type === 'dropdown' && '(comma-separated)'}
                          </label>
                          <input
                            type="text"
                            value={col.options || ''}
                            onChange={(e) => updateNewSheetColumn(index, 'options', e.target.value)}
                            placeholder={col.type === 'dropdown' ? 'opt1, opt2, opt3' : 'N/A'}
                            disabled={col.type !== 'dropdown'}
                            className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                          />
                        </div>

                        <div className="col-span-1 flex items-end justify-center">
                          <button
                            onClick={() => removeColumnFromNewSheet(index)}
                            disabled={newSheetColumns.length === 1}
                            className="p-2 hover:bg-red-600 rounded text-red-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Delete column"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {newSheetColumns.length > 0 && (
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-500">
                        💡 Drag the <GripVertical className="inline" size={14} /> handle to reorder columns
                      </p>
                    </div>
                  )}
                  
                  {/* Invisible marker for auto-scroll */}
                  <div ref={newSheetColumnsEndRef} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowAddSheetModal(false);
                  setNewSheetName('');
                  setNewSheetColumns([{ key: '', label: '', type: 'text', options: '' }]);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSheet}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                Create Sheet
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditSheetModal && editingSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1a1f2e] rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Edit Sheet</h2>
              <button
                onClick={() => {
                  setShowEditSheetModal(false);
                  setEditingSheet(null);
                  setEditSheetName('');
                  setEditSheetColumns([]);
                }}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400"
              >
                <X size={20} />
              </button>
            </div>

            <div ref={editSheetScrollContainerRef} className="p-6 space-y-6 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sheet Name
                </label>
                <input
                  type="text"
                  value={editSheetName}
                  onChange={(e) => setEditSheetName(e.target.value)}
                  placeholder="e.g., Equipment, Materials, etc."
                  className="w-full px-4 py-2 bg-[#0f1419] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-300">
                    Columns
                  </label>
                  <button
                    onClick={addColumnToEditSheet}
                    className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <Plus size={16} />
                    Add Column
                  </button>
                </div>

                <div className="space-y-3">
                  {editSheetColumns.map((col, index) => (
                    <div 
                      key={index} 
                      className={`bg-[#0f1419] p-4 rounded-lg border transition-all duration-200 ${
                        draggedColumnIndex === index 
                          ? 'border-green-500 opacity-50 scale-95' 
                          : dragOverIndex === index 
                            ? 'border-blue-500 border-dashed bg-blue-500/5' 
                            : 'border-gray-700'
                      }`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-1 flex justify-center">
                          <div 
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-600 rounded transition-colors"
                            title="Drag to reorder"
                          >
                            <GripVertical size={16} className="text-gray-400" />
                          </div>
                        </div>

                        <div className="col-span-4">
                          <label className="block text-xs text-gray-400 mb-1">Column Label</label>
                          <input
                            type="text"
                            value={col.label}
                            onChange={(e) => updateEditSheetColumn(index, 'label', e.target.value)}
                            placeholder="e.g., Item Name"
                            className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        <div className="col-span-3">
                          <label className="block text-xs text-gray-400 mb-1">Type</label>
                          <select
                            value={col.type}
                            onChange={(e) => updateEditSheetColumn(index, 'type', e.target.value)}
                            className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="text">Text</option>
                            <option value="dropdown">Dropdown</option>
                            <option value="user_dropdown">User Dropdown</option>
                            <option value="timestamp">Timestamp</option>
                          </select>
                        </div>

                        <div className="col-span-3">
                          <label className="block text-xs text-gray-400 mb-1">
                            Options {col.type === 'dropdown' && '(comma-separated)'}
                          </label>
                          <input
                            type="text"
                            value={col.options || ''}
                            onChange={(e) => updateEditSheetColumn(index, 'options', e.target.value)}
                            placeholder={col.type === 'dropdown' ? 'opt1, opt2, opt3' : 'N/A'}
                            disabled={col.type !== 'dropdown'}
                            className="w-full px-3 py-2 bg-[#1a1f2e] border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                          />
                        </div>

                        <div className="col-span-1 flex items-end justify-center">
                          <button
                            onClick={() => removeColumnFromEditSheet(index)}
                            disabled={editSheetColumns.length === 1}
                            className="p-2 hover:bg-red-600 rounded text-red-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Delete column"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {editSheetColumns.length > 0 && (
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-500">
                        💡 Drag the <GripVertical className="inline" size={14} /> handle to reorder columns
                      </p>
                    </div>
                  )}
                  
                  {/* Invisible marker for auto-scroll */}
                  <div ref={editSheetColumnsEndRef} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowEditSheetModal(false);
                  setEditingSheet(null);
                  setEditSheetName('');
                  setEditSheetColumns([]);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSheet}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabInventory;
